<template>
  <div class="tcl-page" ref="tclPageRoot" :style="pageStyle">
    <!-- ===== Toolbar ===== -->
    <v-toolbar color="primary" density="comfortable" class="tcl-toolbar text-white">
      <v-btn variant="text" prepend-icon="mdi-arrow-left" data-testid="tcl-back" @click="goBack">กลับ</v-btn>
      <v-toolbar-title class="tcl-title">
        Tax Calculate — รายการสูตร
        <span v-if="versionCode" class="tcl-sub">· {{ versionCode }}</span>
      </v-toolbar-title>
      <v-spacer />
      <v-chip v-if="isActiveVersion" size="small" color="white" variant="elevated" class="mr-2 tcl-text-primary">
        <v-icon start size="14">mdi-circle-slice-8</v-icon> ACTIVE
      </v-chip>
      <v-chip v-if="!loading && !loadError && !isNewVersion" size="small" color="white" variant="tonal" class="mr-2">
        {{ formulas.length }} สูตร
      </v-chip>
    </v-toolbar>

    <!-- ===== Create-version (route .../new) ===== -->
    <div v-if="isNewVersion" class="tcl-center">
      <v-card class="tcl-newcard" variant="outlined">
        <v-card-title class="text-subtitle-1">สร้าง Tax Calculate เวอร์ชันใหม่</v-card-title>
        <v-card-text>
          <p class="text-body-2 mb-4 text-medium-emphasis">
            ตั้งรหัสเวอร์ชันก่อน แล้วจึงเพิ่มสูตรเข้าไปทีละตัวได้
          </p>
          <v-text-field
            v-model="newVersion.code"
            label="Version Code *"
            placeholder="เช่น CALC-2569-01"
            density="compact"
            variant="outlined"
            data-testid="tcl-newversion-code"
          />
          <v-text-field
            v-model="newVersion.description"
            label="คำอธิบาย"
            density="compact"
            variant="outlined"
            hide-details
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="goBack">ยกเลิก</v-btn>
          <v-btn
            color="primary"
            variant="flat"
            :loading="saving"
            :disabled="!String(newVersion.code || '').trim()"
            data-testid="tcl-create-version"
            @click="createVersion"
          >สร้างเวอร์ชัน</v-btn>
        </v-card-actions>
      </v-card>
    </div>

    <div v-else-if="loadError" class="tcl-center">
      <v-icon size="42" color="error">mdi-alert-circle-outline</v-icon>
      <span class="mt-3 text-error">{{ loadError }}</span>
      <v-btn class="mt-4" variant="tonal" color="primary" @click="loadAll">ลองอีกครั้ง</v-btn>
    </div>
    <div v-else-if="loading" class="tcl-center">
      <v-progress-circular indeterminate color="primary" size="48" />
      <span class="mt-3 text-grey">กำลังโหลดสูตร…</span>
    </div>

    <template v-else>
      <!-- ===== Actions ===== -->
      <div class="tcl-actions">
        <v-btn size="small" color="primary" variant="flat" prepend-icon="mdi-plus" data-testid="tcl-add" @click="addFormula">
          เพิ่มสูตร
        </v-btn>
        <v-spacer />
        <span class="tcl-hint">
          <v-icon size="14">mdi-information-outline</v-icon>
          ลูกศร ↑↓ = ลำดับที่แสดงเท่านั้น (ไม่มีผลต่อการคำนวณ) · การเรียง / ลบ / ทำสำเนา บันทึกทันที
        </span>
      </div>

      <!-- ===== Formula list ===== -->
      <div class="tcl-list" data-testid="tcl-list">
        <v-alert v-if="!formulas.length" type="info" variant="tonal" density="compact" class="mb-3">
          ยังไม่มีสูตรในเวอร์ชันนี้ — กด “เพิ่มสูตร” เพื่อสร้างสูตรแรก (จะเปิดหน้า Blockly ให้ประกอบสูตร)
        </v-alert>

        <v-card
          v-for="(f, idx) in formulas"
          :key="f.formulaID"
          class="tcl-row"
          variant="outlined"
          :data-testid="`tcl-row-${idx}`"
          @click="editFormula(f)"
        >
          <div class="tcl-row-main">
            <div class="tcl-order" @click.stop>
              <v-btn icon size="x-small" variant="text" :disabled="idx === 0 || busy" @click="moveFormula(idx, -1)">
                <v-icon size="18">mdi-chevron-up</v-icon>
                <v-tooltip activator="parent" location="top">ย้ายขึ้น (ลำดับที่แสดงเท่านั้น)</v-tooltip>
              </v-btn>
              <span class="tcl-order-num">{{ idx + 1 }}</span>
              <v-btn icon size="x-small" variant="text" :disabled="idx === formulas.length - 1 || busy" @click="moveFormula(idx, 1)">
                <v-icon size="18">mdi-chevron-down</v-icon>
                <v-tooltip activator="parent" location="top">ย้ายลง (ลำดับที่แสดงเท่านั้น)</v-tooltip>
              </v-btn>
            </div>

            <div class="tcl-row-body">
              <div class="tcl-row-head">
                <v-icon size="16" color="primary" class="mr-1">mdi-function-variant</v-icon>
                <span class="tcl-name" :data-testid="`tcl-name-${idx}`">{{ f.name || f.outputVar || '—' }}</span>
                <code class="tcl-ov ml-2" :data-testid="`tcl-outputvar-${idx}`">{{ f.outputVar || '—' }}</code>
                <v-chip v-if="!f.isActive" size="x-small" variant="tonal" color="warning" class="ml-1">ปิดใช้งาน</v-chip>
                <span v-if="f.notes" class="tcl-notes">{{ f.notes }}</span>
              </div>
              <div class="tcl-row-expr">
                <span class="tcl-eq">=</span>
                <code class="tcl-expr" :data-testid="`tcl-expr-${idx}`">{{ f.expression || '(ว่าง)' }}</code>
              </div>
              <div v-if="refsOf(f).length" class="tcl-refs">
                <v-icon size="13">mdi-puzzle-outline</v-icon>
                ใช้สูตรย่อย:
                <code v-for="r in refsOf(f)" :key="r" class="tcl-ref-chip">{{ r }}</code>
              </div>
            </div>

            <div class="tcl-row-actions" @click.stop>
              <v-btn
                icon
                size="small"
                variant="text"
                color="primary"
                :disabled="!canPreview"
                :title="previewDisabledReason"
                :data-testid="`tcl-preview-${idx}`"
                @click="togglePreview(f)"
              >
                <v-icon>mdi-play-circle-outline</v-icon>
                <v-tooltip activator="parent" location="top">ดูผล (preview) สูตรนี้</v-tooltip>
              </v-btn>
              <v-btn
                size="small"
                variant="tonal"
                color="primary"
                prepend-icon="mdi-shape-outline"
                :data-testid="`tcl-edit-${idx}`"
                @click="editFormula(f)"
              >แก้ไขสูตร</v-btn>
              <v-btn icon size="small" variant="text" :disabled="busy" @click="duplicateFormula(f)">
                <v-icon>mdi-content-copy</v-icon>
                <v-tooltip activator="parent" location="top">ทำสำเนา</v-tooltip>
              </v-btn>
              <v-btn icon size="small" variant="text" color="error" :disabled="busy" :data-testid="`tcl-del-${idx}`" @click="askDelete(f)">
                <v-icon>mdi-delete-outline</v-icon>
                <v-tooltip activator="parent" location="top">ลบ</v-tooltip>
              </v-btn>
            </div>
          </div>

          <!-- inline preview -->
          <v-expand-transition>
            <div v-if="previews[f.formulaID] && previews[f.formulaID].open" class="tcl-preview" @click.stop>
              <div class="tcl-preview-head">
                <v-icon size="16" class="mr-1" color="primary">mdi-calculator-variant-outline</v-icon>
                <strong>ทดลองคำนวณ “{{ f.outputVar }}”</strong>
                <span class="tcl-preview-hint">— ป้อนค่าแล้วกดคำนวณ</span>
              </div>
              <div class="tcl-preview-fields">
                <template v-for="fld in previews[f.formulaID].fields" :key="fld.model">
                  <!-- an ITERATED rule — the nested rows a `[]` path walks: subItems (each with its
                       own asset category and therefore its own เหมา rate), and inside each of them the
                       individual items. Two levels, because `subItems[].items[].amount` is two `[]`. -->
                  <div v-if="fld.kind === 'formData'" class="tcl-items" :data-testid="`tcl-fd-${idx}`">
                    <div class="tcl-items-head">
                      <v-icon size="15" class="mr-1">mdi-file-tree</v-icon>
                      <strong>{{ fld.label }}</strong>
                      <span class="tcl-items-hint">— แต่ละหมวดมีอัตราเหมาของตัวเอง และมีรายการย่อยได้หลายรายการ</span>
                      <v-spacer />
                      <v-btn
                        size="x-small"
                        variant="tonal"
                        color="primary"
                        prepend-icon="mdi-plus"
                        :data-testid="`tcl-sub-add-${idx}`"
                        @click="addSubItem(f, fld)"
                      >เพิ่มหมวด</v-btn>
                    </div>
                    <div
                      v-for="(sub, si) in subItemRows(f, fld)"
                      :key="`${fld.model}-${si}`"
                      class="tcl-sub"
                    >
                      <div class="tcl-sub-head">
                        <v-text-field
                          v-model="sub.labelTh"
                          :placeholder="`หมวดที่ ${si + 1}`"
                          label="ชื่อหมวด (subItems[].labelTh)"
                          density="compact"
                          variant="outlined"
                          hide-details
                          class="tcl-item-name"
                        />
                        <v-select
                          v-model="sub.subTypeCode"
                          :items="expenseOptionItems"
                          item-title="title"
                          item-value="value"
                          label="ประเภททรัพย์สิน (subTypeCode)"
                          density="compact"
                          variant="outlined"
                          hide-details
                          class="tcl-item-option"
                        />
                        <!-- only a หักตามจริง category has an expense to type; a เหมา one derives it -->
                        <v-text-field
                          v-if="sub.subTypeCode === actualOption"
                          v-model.number="sub.actualExpense"
                          type="number"
                          label="ค่าใช้จ่ายจริง"
                          density="compact"
                          variant="outlined"
                          hide-details
                          class="tcl-item-actual"
                        />
                        <span class="tcl-sub-rate">rate = {{ rateFor(sub.subTypeCode) }}</span>
                        <v-btn
                          icon
                          size="x-small"
                          variant="text"
                          :disabled="subItemRows(f, fld).length <= 1"
                          :data-testid="`tcl-sub-del-${idx}-${si}`"
                          @click="removeSubItem(f, fld, si)"
                        >
                          <v-icon size="18">mdi-close</v-icon>
                          <v-tooltip activator="parent" location="top">ลบหมวดนี้</v-tooltip>
                        </v-btn>
                      </div>
                      <div
                        v-for="(row, ri) in entryRows(sub)"
                        :key="`${fld.model}-${si}-${ri}`"
                        class="tcl-item-row"
                      >
                        <v-text-field
                          v-model="row.labelTh"
                          :placeholder="`รายการที่ ${ri + 1}`"
                          label="ชื่อรายการ (items[].labelTh)"
                          density="compact"
                          variant="outlined"
                          hide-details
                          class="tcl-item-name"
                        />
                        <v-text-field
                          v-model.number="row.amount"
                          type="number"
                          label="จำนวนเงิน (items[].amount)"
                          density="compact"
                          variant="outlined"
                          hide-details
                          class="tcl-item-amount"
                        />
                        <v-btn
                          icon
                          size="x-small"
                          variant="text"
                          :disabled="entryRows(sub).length <= 1"
                          :data-testid="`tcl-item-del-${idx}-${si}-${ri}`"
                          @click="removeEntry(sub, ri)"
                        >
                          <v-icon size="18">mdi-close</v-icon>
                          <v-tooltip activator="parent" location="top">ลบรายการนี้</v-tooltip>
                        </v-btn>
                      </div>
                      <div class="tcl-sub-foot">
                        <v-btn
                          size="x-small"
                          variant="text"
                          color="primary"
                          prepend-icon="mdi-plus"
                          :data-testid="`tcl-item-add-${idx}-${si}`"
                          @click="addEntry(sub)"
                        >เพิ่มรายการในหมวดนี้</v-btn>
                        <v-spacer />
                        <span class="tcl-sub-total">รวมหมวดนี้ ฿{{ formatMoney(subItemAmount(sub)) }}</span>
                      </div>
                    </div>
                  </div>
                  <v-select
                    v-else-if="fld.kind === 'option'"
                    v-model="previews[f.formulaID].values[fld.model]"
                    :items="expenseOptionItems"
                    item-title="title"
                    item-value="value"
                    :label="fld.label"
                    density="compact"
                    variant="outlined"
                    hide-details
                    class="tcl-pv-field"
                  />
                  <v-text-field
                    v-else
                    v-model.number="previews[f.formulaID].values[fld.model]"
                    type="number"
                    :label="fld.label"
                    density="compact"
                    variant="outlined"
                    hide-details
                    class="tcl-pv-field"
                  />
                </template>
                <v-btn
                  color="primary"
                  size="small"
                  variant="flat"
                  :loading="previews[f.formulaID].loading"
                  :disabled="!canPreview"
                  prepend-icon="mdi-play"
                  :data-testid="`tcl-run-preview-${idx}`"
                  @click="runPreview(f)"
                >คำนวณ</v-btn>
              </div>
              <v-alert v-if="previews[f.formulaID].error" type="error" variant="tonal" density="compact" class="mt-2">
                {{ previews[f.formulaID].error }}
              </v-alert>
              <div v-if="previews[f.formulaID].result" class="tcl-preview-result">
                <div class="tcl-result-amount" :data-testid="`tcl-result-${idx}`">
                  {{ previewHeadline(f) }}
                  <strong>฿{{ formatMoney(previews[f.formulaID].result.value) }}</strong>
                </div>
                <!-- per-entry lines: seeing "คอนโด 72,000 / ตึกแถว 180,000" instead of one lump IS the
                     reason the author chose a per-item aggregate over a single per-category rate. -->
                <div v-if="itemBreakdownOf(f).length" class="tcl-breakdown" :data-testid="`tcl-breakdown-${idx}`">
                  <div class="tcl-breakdown-head">แยกตามรายการ</div>
                  <div v-for="(line, li) in itemBreakdownOf(f)" :key="`ib-${li}`" class="tcl-breakdown-line">
                    <span class="tcl-bd-label">{{ line.label || line.itemId }}</span>
                    <span class="tcl-bd-amount">{{ formatMoney(line.amount) }}</span>
                    <span class="tcl-bd-rate">{{ line.rate > 0 ? `เหมา ${Math.round(line.rate * 100)}%` : 'ตามจริง' }}</span>
                    <span class="tcl-bd-value">= ฿{{ formatMoney(line.value) }}</span>
                  </div>
                </div>
                <div v-for="line in previews[f.formulaID].result.contributing" :key="line.outputVar" class="tcl-result-line">
                  <code>{{ line.outputVar }}</code> = {{ formatMoney(line.value) }}
                  <span class="tcl-result-expr">({{ line.expression }})</span>
                </div>
                <div v-for="(w, i) in previews[f.formulaID].result.warnings" :key="`pw-${i}`" class="tcl-result-warn">
                  <v-icon size="13" color="warning">mdi-alert-outline</v-icon> {{ w }}
                </div>
              </div>
            </div>
          </v-expand-transition>
        </v-card>
      </div>

      <!-- ===== Version-level settings ===== -->
      <v-expansion-panels v-model="panel" multiple class="tcl-panels">
        <!-- Constants -->
        <v-expansion-panel value="constants">
          <v-expansion-panel-title>
            <v-icon size="18" class="mr-2">mdi-alpha-c-box-outline</v-icon>
            ค่าคงที่ (Constants) — {{ constants.length }}
            <v-chip v-if="constantsDirty" size="x-small" color="warning" variant="tonal" class="ml-2">ยังไม่บันทึก</v-chip>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <div class="tcl-sec-actions">
              <v-btn size="x-small" variant="tonal" color="primary" prepend-icon="mdi-plus" @click="addConstant">เพิ่มค่าคงที่</v-btn>
              <v-spacer />
              <v-btn
                size="small"
                color="primary"
                variant="flat"
                :disabled="!constantsDirty"
                :loading="savingConstants"
                data-testid="tcl-save-constants"
                @click="saveConstants"
              >บันทึกค่าคงที่</v-btn>
            </div>
            <div v-for="(c, ci) in constants" :key="c._uid" class="tcl-const-row">
              <v-text-field v-model="c.constKey" label="ConstKey (UPPER_SNAKE)" density="compact" variant="outlined" hide-details class="tcl-const-key" @update:model-value="constantsDirty = true" />
              <v-text-field v-model.number="c.constValue" type="number" label="ค่า" density="compact" variant="outlined" hide-details class="tcl-const-val" @update:model-value="constantsDirty = true" />
              <v-text-field v-model="c.notes" label="หมายเหตุ" density="compact" variant="outlined" hide-details class="tcl-const-notes" @update:model-value="constantsDirty = true" />
              <v-btn icon size="x-small" variant="text" color="error" @click="removeConstant(ci)">
                <v-icon size="18">mdi-delete-outline</v-icon>
              </v-btn>
            </div>
          </v-expansion-panel-text>
        </v-expansion-panel>

        <!-- Brackets -->
        <v-expansion-panel value="brackets">
          <v-expansion-panel-title>
            <v-icon size="18" class="mr-2">mdi-stairs</v-icon>
            ขั้นบันไดภาษี (Brackets) — {{ brackets.length }}
            <v-chip v-if="bracketsDirty" size="x-small" color="warning" variant="tonal" class="ml-2">ยังไม่บันทึก</v-chip>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <div class="tcl-sec-actions">
              <v-btn size="x-small" variant="tonal" color="primary" prepend-icon="mdi-plus" @click="addBracket">เพิ่มขั้น</v-btn>
              <v-spacer />
              <v-btn
                size="small"
                color="primary"
                variant="flat"
                :disabled="!bracketsDirty"
                :loading="savingBrackets"
                data-testid="tcl-save-brackets"
                @click="saveBrackets"
              >บันทึกขั้นบันได</v-btn>
            </div>
            <div class="tcl-brk-head">
              <span>เงินได้สุทธิตั้งแต่</span><span>ถึง (ว่าง = ไม่จำกัด)</span><span>อัตรา (0.05 = 5%)</span><span></span>
            </div>
            <div v-for="(b, bi) in brackets" :key="b._uid" class="tcl-brk-row">
              <v-text-field v-model.number="b.lowerBound" type="number" density="compact" variant="outlined" hide-details @update:model-value="bracketsDirty = true" />
              <v-text-field v-model="b.upperBound" type="number" placeholder="ไม่จำกัด" density="compact" variant="outlined" hide-details @update:model-value="bracketsDirty = true" />
              <v-text-field v-model.number="b.rate" type="number" step="0.01" density="compact" variant="outlined" hide-details @update:model-value="bracketsDirty = true" />
              <v-btn icon size="x-small" variant="text" color="error" @click="removeBracket(bi)">
                <v-icon size="18">mdi-delete-outline</v-icon>
              </v-btn>
            </div>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </template>

    <!-- delete confirm -->
    <v-dialog v-model="deleteDialog.open" max-width="420">
      <v-card>
        <v-card-title>ลบสูตร</v-card-title>
        <v-card-text>
          ลบสูตร <code>{{ deleteDialog.formula && deleteDialog.formula.outputVar }}</code> ออกจากเวอร์ชันนี้?
          <div v-if="deleteDialog.usedBy.length" class="mt-3">
            <v-alert type="warning" variant="tonal" density="compact">
              มีสูตรอื่นอ้างถึงอยู่: <code v-for="u in deleteDialog.usedBy" :key="u" class="tcl-ref-chip">{{ u }}</code>
              — สูตรเหล่านั้นจะพังจนกว่าจะแก้
            </v-alert>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="deleteDialog.open = false">ยกเลิก</v-btn>
          <v-btn color="error" variant="tonal" :loading="busy" @click="confirmDelete">ลบ</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" location="bottom" timeout="3200">
      {{ snackbar.message }}
    </v-snackbar>
  </div>
</template>

<script>
import { taxCalculateApi } from '../services/taxCalculateApi'
import {
  taxCalculateStore,
  EXPENSE_40_5_OPTIONS,
  FORMULA_FUNCTIONS,
} from '../utils/taxCalculateStore'
// The preview panel's whole derivation — what to ASK FOR and what to SEND — lives in a headless
// module so it can be RUN by a test. It used to be inline here, which is why nothing caught the panel
// scanning a path into its segments (F3) or never sending formData (F4).
import {
  ACTUAL_OPTION,
  identifiersOf,
  iteratedRoots,
  derivePreviewFields,
  blankSubItem,
  entriesOf,
  blankEntry,
  rateFor,
  subItemAmount,
  subItemLabel,
  buildPreviewPayload,
} from '../utils/taxPreviewInputs'

export default {
  name: 'TaxCalculateList',
  data() {
    return {
      loading: true,
      loadError: '',
      saving: false,
      savingConstants: false,
      savingBrackets: false,
      busy: false,
      configVersionId: this.$route.params.configVersionId,
      calcVersionId: (() => {
        const raw = this.$route.params.calcVersionId
        if (raw === 'new') return null
        const n = Number(raw)
        return Number.isFinite(n) ? n : null
      })(),
      newVersion: { code: '', description: '' },
      full: null,
      // The app-shell breadcrumb bar is position:fixed (NavBar.vue .fixed-breadcrumbs, top:64px) and
      // reserves no space, so this page measures it and pads below — same as the Formula editor.
      topPad: 48,
      panel: [],
      previews: {},
      constantsDirty: false,
      bracketsDirty: false,
      deleteDialog: { open: false, formula: null, usedBy: [] },
      snackbar: { show: false, color: 'success', message: '' },
      store: taxCalculateStore.state,
    }
  },
  computed: {
    pageStyle() {
      return { paddingTop: this.topPad + 'px' }
    },
    formulas() {
      return this.store.formulas
    },
    constants() {
      return this.store.constants
    },
    brackets() {
      return this.store.brackets
    },
    isNewVersion() {
      return this.calcVersionId == null && this.$route.params.calcVersionId === 'new'
    },
    versionCode() {
      return (this.full && this.full.version && this.full.version.versionCode) || ''
    },
    isActiveVersion() {
      return !!(this.full && this.full.version && this.full.version.isActive)
    },
    canPreview() {
      return !this.constantsDirty && !this.busy && this.calcVersionId != null
    },
    previewDisabledReason() {
      if (this.constantsDirty) return 'บันทึกค่าคงที่ก่อน จึงจะ preview ตามอัตราล่าสุดได้'
      return undefined
    },
    /** Exposed so the entry row's "ค่าใช้จ่ายจริง" field tests the SAME code the request writes. */
    actualOption() {
      return ACTUAL_OPTION
    },
    expenseOptionItems() {
      return EXPENSE_40_5_OPTIONS.map((o) => ({
        value: o.optionCode,
        title: o.rate != null ? `${o.label} (${Math.round(o.rate * 100)}%)` : o.label,
      }))
    },
    outputVarSet() {
      const s = new Set()
      this.formulas.forEach((f) => {
        const v = String(f.outputVar || '').trim().toLowerCase()
        if (v) s.add(v)
      })
      return s
    },
  },
  created() {
    if (!this.isNewVersion) this.loadAll()
    else this.loading = false
  },
  mounted() {
    this._onResize = () => this.updateLayoutInsets()
    window.addEventListener('resize', this._onResize)
    // Measure after the breadcrumb has laid out (it lives in a sibling app-shell component).
    this.$nextTick(() => this.updateLayoutInsets())
  },
  beforeUnmount() {
    if (this._onResize) window.removeEventListener('resize', this._onResize)
  },
  methods: {
    /** Pad this page below the app-shell's fixed breadcrumb bar so the toolbar isn't overlapped. */
    updateLayoutInsets() {
      const root = this.$refs.tclPageRoot
      if (!root) return
      // getBoundingClientRect().top is the border-box top — unaffected by our own paddingTop.
      const naturalTop = root.getBoundingClientRect().top
      let breadcrumbBottom = naturalTop
      const bc = document.querySelector('.fixed-breadcrumbs')
      if (bc) {
        const r = bc.getBoundingClientRect()
        if (r.height > 0) breadcrumbBottom = r.bottom // skip when hidden (0-height)
      }
      const pad = Math.max(0, Math.round(breadcrumbBottom - naturalTop))
      if (pad !== this.topPad) this.topPad = pad
    },
    async loadAll() {
      this.loading = true
      this.loadError = ''
      try {
        const full = await taxCalculateApi.getFull(this.calcVersionId)
        if (!full || !full.version) throw new Error('ไม่พบ calc version นี้')
        this.full = full
        taxCalculateStore.loadFull(full)
        this.previews = {}
        this.constantsDirty = false
        this.bracketsDirty = false
      } catch (err) {
        this.loadError = (err && err.message) ? err.message : 'โหลดข้อมูลไม่สำเร็จ'
      } finally {
        this.loading = false
        // Re-measure once content is in: the breadcrumb chain can change height after the version loads.
        this.$nextTick(() => this.updateLayoutInsets())
      }
    },
    async createVersion() {
      const code = String(this.newVersion.code || '').trim()
      if (!code) return
      this.saving = true
      try {
        const created = await taxCalculateApi.createVersion({
          taxConfigVersionID: Number(this.configVersionId),
          versionCode: code,
          description: this.newVersion.description || '',
          isActive: false,
        })
        const newId = created && created.calcVersionID
        if (newId == null) throw new Error('สร้างเวอร์ชันไม่สำเร็จ (missing id)')
        this.calcVersionId = Number(newId)
        this.$router.replace(`/TaxCalculate/${this.configVersionId}/${newId}`)
        await this.loadAll()
        this.showSnack('สร้างเวอร์ชันแล้ว — เพิ่มสูตรได้เลย', 'success')
      } catch (err) {
        this.showSnack((err && err.message) || 'สร้างเวอร์ชันไม่สำเร็จ', 'error')
      } finally {
        this.saving = false
      }
    },

    // ── navigation to the Formula (Blockly) level ──
    editFormula(f) {
      this.$router.push(`/TaxCalculate/${this.configVersionId}/${this.calcVersionId}/formula/${f.formulaID}`)
    },
    addFormula() {
      this.$router.push(`/TaxCalculate/${this.configVersionId}/${this.calcVersionId}/formula/new`)
    },

    // ── immediate per-formula ops (FormulaIDs must stay stable → never bulk-replace) ──
    /** Request body for updateFormula that preserves everything except the overridden fields. */
    formulaBody(f, override = {}) {
      return taxCalculateStore.buildFormulaRequest({
        name: f.name,
        expression: f.expression,
        expressionGraphJson: f.expressionGraphJson ?? null,
        outputVar: f.outputVar,
        evalOrder: f.evalOrder,
        notes: f.notes,
        isActive: f.isActive,
        ...override,
      }, override.evalOrder != null ? override.evalOrder : f.evalOrder)
    },
    async moveFormula(idx, delta) {
      const to = idx + delta
      if (to < 0 || to >= this.formulas.length || this.busy) return
      const a = this.formulas[idx]
      const b = this.formulas[to]
      this.busy = true
      try {
        // Swap the two rows' evalOrder (2 updates) instead of reindexing via bulk-save, which would
        // delete+reinsert every formula and regenerate the FormulaIDs the Formula editor links to.
        const aOrder = Number(a.evalOrder) || 0
        const bOrder = Number(b.evalOrder) || 0
        const [oa, ob] = aOrder === bOrder ? [idx === 0 ? aOrder : aOrder - 1, bOrder] : [bOrder, aOrder]
        await taxCalculateApi.updateFormula(a.formulaID, this.formulaBody(a, { evalOrder: oa }))
        await taxCalculateApi.updateFormula(b.formulaID, this.formulaBody(b, { evalOrder: ob }))
        await this.loadAll()
      } catch (err) {
        this.showSnack((err && err.message) || 'ย้ายลำดับไม่สำเร็จ', 'error')
      } finally {
        this.busy = false
      }
    },
    async duplicateFormula(f) {
      this.busy = true
      try {
        const base = String(f.outputVar || 'formula')
        let code = `${base}_copy`
        let n = 2
        while (this.outputVarSet.has(code.toLowerCase())) { code = `${base}_copy${n}`; n += 1 }
        await taxCalculateApi.createFormula(this.calcVersionId, this.formulaBody(f, {
          name: `${f.name || base} (สำเนา)`,
          outputVar: code,
          evalOrder: (Number(f.evalOrder) || 0) + 1,
        }))
        await this.loadAll()
        this.showSnack(`ทำสำเนาเป็น ${code} แล้ว`, 'success')
      } catch (err) {
        this.showSnack((err && err.message) || 'ทำสำเนาไม่สำเร็จ', 'error')
      } finally {
        this.busy = false
      }
    },
    askDelete(f) {
      this.deleteDialog = { open: true, formula: f, usedBy: this.usedBy(f) }
    },
    /** Other formulas whose expression references this one's outputVar. */
    usedBy(f) {
      const ov = String(f.outputVar || '').trim().toLowerCase()
      if (!ov) return []
      return this.formulas
        .filter((x) => x.formulaID !== f.formulaID && this.identifiers(x.expression).some((id) => id.toLowerCase() === ov))
        .map((x) => x.outputVar)
    },
    async confirmDelete() {
      const f = this.deleteDialog.formula
      if (!f) return
      this.busy = true
      try {
        await taxCalculateApi.deleteFormula(f.formulaID)
        this.deleteDialog.open = false
        await this.loadAll()
        this.showSnack('ลบสูตรแล้ว', 'success')
      } catch (err) {
        this.showSnack((err && err.message) || 'ลบไม่สำเร็จ', 'error')
      } finally {
        this.busy = false
      }
    },

    // ── version-level settings ──
    addConstant() {
      this.store.constants.push(taxCalculateStore.createBlankConstant())
      this.constantsDirty = true
    },
    removeConstant(idx) {
      this.store.constants.splice(idx, 1)
      this.constantsDirty = true
    },
    async saveConstants() {
      this.savingConstants = true
      try {
        await taxCalculateApi.bulkSaveConstants(this.calcVersionId, taxCalculateStore.buildConstantRequests(this.constants))
        await this.loadAll()
        this.showSnack('บันทึกค่าคงที่แล้ว', 'success')
      } catch (err) {
        this.showSnack((err && err.message) || 'บันทึกค่าคงที่ไม่สำเร็จ', 'error')
      } finally {
        this.savingConstants = false
      }
    },
    addBracket() {
      const last = this.brackets[this.brackets.length - 1]
      this.store.brackets.push(taxCalculateStore.createBlankBracket({
        lowerBound: last && last.upperBound != null ? Number(last.upperBound) : 0,
        upperBound: null,
        rate: 0,
        sortOrder: this.brackets.length + 1,
      }))
      this.bracketsDirty = true
    },
    removeBracket(idx) {
      this.store.brackets.splice(idx, 1)
      this.bracketsDirty = true
    },
    async saveBrackets() {
      this.savingBrackets = true
      try {
        this.store.brackets.forEach((b, i) => { b.sortOrder = i + 1 })
        await taxCalculateApi.bulkSaveBrackets(this.calcVersionId, taxCalculateStore.buildBracketRequests(this.brackets))
        await this.loadAll()
        this.showSnack('บันทึกขั้นบันไดแล้ว', 'success')
      } catch (err) {
        this.showSnack((err && err.message) || 'บันทึกขั้นบันไดไม่สำเร็จ', 'error')
      } finally {
        this.savingBrackets = false
      }
    },

    // ── formula reference helpers (display) ──
    identifiers(expr) {
      return identifiersOf(expr, FORMULA_FUNCTIONS)
    },
    iteratedRoots(closure) {
      return iteratedRoots(closure)
    },
    // ── the NESTED rows a `[]` path walks ────────────────────────────────────
    // Two levels, because `subItems[].items[].amount` is two `[]`: a subItem (one asset CATEGORY — it
    // carries `subTypeCode` and therefore the `rate`) and, inside it, the individual items. A flat
    // list could only ever fill one of them, which is how a §40(5) formula previewed ฿0.
    // The shapes and the derivation live in utils/taxPreviewInputs.js (headless, under test).
    blankSubItem() { return blankSubItem() },
    subItemRows(f, fld) {
      const p = this.previews[f.formulaID]
      const rows = p && p.values[fld.model]
      return Array.isArray(rows) ? rows : []
    },
    entryRows(sub) { return entriesOf(sub) },
    addSubItem(f, fld) { this.subItemRows(f, fld).push(blankSubItem()) },
    removeSubItem(f, fld, idx) {
      const rows = this.subItemRows(f, fld)
      if (rows.length > 1) rows.splice(idx, 1)
    },
    addEntry(sub) { entriesOf(sub).push(blankEntry()) },
    removeEntry(sub, idx) {
      const rows = entriesOf(sub)
      if (rows.length > 1) rows.splice(idx, 1)
    },
    subItemLabel(sub, idx) { return subItemLabel(sub, idx) },
    /** The เหมา rate of a category, resolved from THIS version's EXPENSE_RATE_<OPTION> constant. */
    rateFor(optionCode) { return rateFor(optionCode, this.constants) },
    subItemAmount(sub) { return subItemAmount(sub) },
    /** Other formulas THIS formula pulls in as variables. */
    refsOf(f) {
      const self = String(f.outputVar || '').trim().toLowerCase()
      const set = new Set()
      this.identifiers(f.expression).forEach((id) => {
        const low = id.toLowerCase()
        if (low !== self && this.outputVarSet.has(low)) set.add(low)
      })
      return Array.from(set)
    },

    // ── Preview ("call a point") ──
    togglePreview(f) {
      const id = f.formulaID
      if (this.previews[id] && this.previews[id].open) {
        this.previews[id].open = false
        return
      }
      const fields = this.deriveFields(f)
      const values = (this.previews[id] && this.previews[id].values) || {}
      fields.forEach((fld) => {
        if (values[fld.model] != null) return
        // An iterated rule opens with ONE empty category holding ONE empty row: a zero-row list would
        // hide the whole feature behind an "เพิ่ม" button and send empty subItems, which the engine
        // reads as "this rule has no data" — i.e. it would preview ฿0 and be RIGHT to.
        if (fld.kind === 'formData') values[fld.model] = [blankSubItem()]
        else values[fld.model] = fld.kind === 'option' ? fld.default : 0
      })
      this.previews[id] = { open: true, loading: false, error: '', result: null, fields, values }
    },
    closureFor(target) {
      const byOv = new Map()
      this.formulas.forEach((f) => {
        const ov = String(f.outputVar || '').trim().toLowerCase()
        if (ov) byOv.set(ov, f)
      })
      const seen = new Set()
      const out = []
      const stack = [target]
      while (stack.length) {
        const f = stack.pop()
        const ov = String(f.outputVar || '').trim().toLowerCase()
        if (!ov || seen.has(ov)) continue
        seen.add(ov)
        out.push(f)
        this.identifiers(f.expression).forEach((id) => {
          const low = id.toLowerCase()
          if (low !== ov && byOv.has(low)) stack.push(byOv.get(low))
        })
      }
      return out
    },
    deriveFields(target) {
      return derivePreviewFields(this.closureFor(target), {
        constKeys: this.constants.map((c) => c.constKey),
        outputVars: Array.from(this.outputVarSet),
        functionNames: FORMULA_FUNCTIONS,
      })
    },
    async runPreview(f) {
      const p = this.previews[f.formulaID]
      if (!p) return
      p.loading = true
      p.error = ''
      p.result = null
      try {
        // `formData` — the NESTED channel a `[]` path resolves against — is built here and ALWAYS
        // sent. Without it the engine projects the flat `inputs` rows into the same structure and
        // attaches a permanent "ส่งมาในรูปแบบเดิม … ตรวจสอบผลลัพธ์ก่อนใช้งาน" warning, on healthy input.
        const payload = buildPreviewPayload(p.fields, p.values, this.constants)
        const res = await taxCalculateApi.previewFormula(this.calcVersionId, f.outputVar, payload)
        p.result = res
        taxCalculateStore.setLastPreview(res)
      } catch (err) {
        p.error = (err && err.message) || 'คำนวณไม่สำเร็จ'
      } finally {
        p.loading = false
      }
    },
    /** The per-entry lines the last preview returned (empty when the target aggregated nothing). */
    itemBreakdownOf(f) {
      const p = this.previews[f.formulaID]
      const lines = p && p.result && p.result.itemBreakdown
      return Array.isArray(lines) ? lines : []
    },
    previewHeadline(f) {
      const p = this.previews[f.formulaID]
      const optField = p && p.fields.find((x) => x.kind === 'option')
      if (optField) {
        const opt = EXPENSE_40_5_OPTIONS.find((o) => o.optionCode === p.values[optField.model])
        if (opt && opt.rate != null) return `ถ้าเลือกแบบเหมา (${opt.label} ${Math.round(opt.rate * 100)}%) หักค่าใช้จ่ายได้ =`
        if (opt) return `${opt.label} — หักค่าใช้จ่ายได้ =`
      }
      return `${f.outputVar} =`
    },

    goBack() {
      this.$router.push('/TaxConfiguration')
    },
    formatMoney(v) {
      const n = Number(v)
      if (!Number.isFinite(n)) return '0'
      return n.toLocaleString('th-TH', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
    },
    showSnack(message, color) {
      this.snackbar = { show: true, color: color || 'success', message }
    },
  },
}
</script>

<style scoped>
.tcl-page {
  display: flex;
  flex-direction: column;
  /* padding-top is set at runtime by updateLayoutInsets() to clear the fixed breadcrumb bar;
     border-box keeps that padding inside min-height so the page gains no phantom scroll. */
  box-sizing: border-box;
  min-height: calc(100vh - 64px);
  background: rgb(var(--v-theme-background));
}
.tcl-title { font-weight: 600; }
.tcl-sub { opacity: 0.85; font-weight: 400; font-size: 0.9rem; }
.tcl-text-primary { color: rgb(var(--v-theme-primary)) !important; }

.tcl-center {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px;
}
.tcl-newcard { width: 100%; max-width: 460px; }

.tcl-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 20px 4px;
}
.tcl-hint {
  font-size: 0.78rem;
  color: rgba(var(--v-theme-on-surface), 0.55);
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.tcl-list { padding: 10px 20px 4px; }

.tcl-row {
  margin-bottom: 10px;
  border-radius: 12px;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.tcl-row:hover {
  border-color: rgb(var(--v-theme-primary));
  box-shadow: 0 2px 10px rgba(var(--v-theme-primary), 0.14);
}
.tcl-row-main { display: flex; align-items: center; gap: 12px; padding: 10px 14px; min-width: 0; }

.tcl-order { display: flex; flex-direction: column; align-items: center; min-width: 34px; flex: none; }
.tcl-order-num { font-size: 0.72rem; color: rgba(var(--v-theme-on-surface), 0.55); line-height: 1; }

.tcl-row-body { flex: 1; min-width: 0; }
.tcl-row-head { display: flex; align-items: center; flex-wrap: wrap; gap: 2px; }
.tcl-name { font-weight: 600; font-size: 0.95rem; }
.tcl-ov {
  font-family: 'JetBrains Mono', 'Roboto Mono', monospace;
  font-size: 0.82rem;
  color: rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-primary), 0.1);
  padding: 0 6px;
  border-radius: 5px;
}
.tcl-notes {
  font-size: 0.76rem;
  color: rgba(var(--v-theme-on-surface), 0.5);
  margin-left: 8px;
  /* flex child: min-width:0 is what actually lets it shrink and ellipsize instead of overflowing */
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tcl-row-expr { display: flex; align-items: baseline; gap: 6px; margin-top: 3px; min-width: 0; }
.tcl-eq { font-weight: 700; color: rgb(var(--v-theme-primary)); flex: none; }
.tcl-expr {
  font-family: 'JetBrains Mono', 'Roboto Mono', monospace;
  font-size: 0.84rem;
  color: rgba(var(--v-theme-on-surface), 0.82);
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tcl-refs {
  margin-top: 4px;
  font-size: 0.74rem;
  color: rgba(var(--v-theme-on-surface), 0.6);
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}
.tcl-ref-chip {
  background: rgba(var(--v-theme-primary), 0.1);
  color: rgb(var(--v-theme-primary));
  padding: 0 6px;
  border-radius: 5px;
  font-size: 0.74rem;
}
/* never let the long notes/expression squeeze the actions — they keep their intrinsic width */
.tcl-row-actions { display: flex; align-items: center; gap: 4px; flex: none; }

.tcl-preview {
  margin: 0 14px 12px;
  padding: 12px;
  border-radius: 10px;
  background: rgba(var(--v-theme-primary), 0.05);
  border: 1px dashed rgba(var(--v-theme-primary), 0.35);
  cursor: default;
}
.tcl-preview-head { display: flex; align-items: center; margin-bottom: 8px; font-size: 0.9rem; }
.tcl-preview-hint { color: rgba(var(--v-theme-on-surface), 0.55); margin-left: 6px; }
.tcl-preview-fields { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; }
.tcl-pv-field { max-width: 260px; min-width: 180px; }
.tcl-preview-result { margin-top: 10px; }
.tcl-result-amount { font-size: 1.05rem; }
.tcl-result-amount strong { color: rgb(var(--v-theme-primary)); font-size: 1.25rem; margin-left: 4px; }
.tcl-result-line { font-size: 0.82rem; color: rgba(var(--v-theme-on-surface), 0.7); margin-top: 2px; }
.tcl-result-expr { color: rgba(var(--v-theme-on-surface), 0.45); }
.tcl-result-warn { font-size: 0.78rem; color: rgb(var(--v-theme-warning)); margin-top: 3px; }

/* Entry list of an aggregated rule — takes the full width of the wrapping field row: a row of four
   inputs cannot share a line with the scalar fields beside it and stay readable. */
.tcl-items { flex: 1 1 100%; border: 1px solid rgba(var(--v-theme-on-surface), 0.12); border-radius: 8px; padding: 10px; }
.tcl-items-head { display: flex; align-items: center; font-size: 0.85rem; margin-bottom: 8px; }
.tcl-items-hint { color: rgba(var(--v-theme-on-surface), 0.55); margin-left: 6px; }
.tcl-item-row { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; margin-bottom: 8px; }
.tcl-item-name { max-width: 190px; min-width: 140px; }
.tcl-item-amount { max-width: 170px; min-width: 130px; }
.tcl-item-option { max-width: 260px; min-width: 190px; }
.tcl-item-actual { max-width: 160px; min-width: 130px; }
/* One subItem = one asset CATEGORY. The left rule + indent is the whole visual argument that the
   rows below it belong to THIS category's rate — the nesting is the data model, so it has to be
   legible at a glance or the panel is just two flat lists that happen to be adjacent. */
.tcl-sub {
  border-left: 3px solid rgba(var(--v-theme-primary), 0.35);
  padding: 8px 0 4px 10px;
  margin-bottom: 10px;
}
.tcl-sub-head { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; margin-bottom: 8px; }
.tcl-sub-foot { display: flex; align-items: center; gap: 8px; }
.tcl-sub-rate { font-size: 0.78rem; color: rgba(var(--v-theme-on-surface), 0.6); font-variant-numeric: tabular-nums; }
.tcl-sub-total { font-size: 0.8rem; color: rgba(var(--v-theme-on-surface), 0.7); font-variant-numeric: tabular-nums; }
.tcl-breakdown { margin-top: 8px; }
.tcl-breakdown-head { font-size: 0.78rem; color: rgba(var(--v-theme-on-surface), 0.55); margin-bottom: 2px; }
.tcl-breakdown-line { display: flex; gap: 10px; font-size: 0.82rem; color: rgba(var(--v-theme-on-surface), 0.75); }
.tcl-bd-label { min-width: 140px; }
.tcl-bd-amount { min-width: 90px; text-align: right; }
.tcl-bd-rate { min-width: 80px; color: rgba(var(--v-theme-on-surface), 0.5); }
.tcl-bd-value { font-weight: 500; }

.tcl-panels { padding: 8px 20px 28px; }
.tcl-sec-actions { display: flex; align-items: center; margin-bottom: 10px; }
.tcl-const-row { display: flex; gap: 10px; align-items: center; margin-bottom: 8px; }
.tcl-const-key { max-width: 280px; }
.tcl-const-val { max-width: 140px; }
.tcl-const-notes { flex: 1; }

.tcl-brk-head,
.tcl-brk-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 40px;
  gap: 10px;
  align-items: center;
}
.tcl-brk-head {
  font-size: 0.74rem;
  color: rgba(var(--v-theme-on-surface), 0.55);
  margin-bottom: 4px;
}
.tcl-brk-row { margin-bottom: 8px; }
</style>
